import { useId } from 'react';
import type { ConfirmDialogProps } from '@/features/common/ConfirmDialog/types/ConfirmDialogProps';
import type { ConfirmTone } from '@/features/common/ConfirmDialog/types/ConfirmTone';
import { useNativeDialog } from '@/features/common/useNativeDialog';
import { common } from '@/i18n/dictionaries/common';
import { useTranslation } from '@/i18n/useTranslation';

const CONFIRM_BUTTON: Record<ConfirmTone, string> = {
  danger: 'btn btn--danger',
  primary: 'btn btn--primary',
};

/**
 * The site's confirmation: a question, what answering yes does, and the two buttons — in place of
 * window.confirm, which the browser draws in its own chrome, in its own language and outside MUD.
 *
 * <p>A native `dialog` opened modally (useNativeDialog), so the focus trap, the inert page,
 * Escape and the focus return are the browser's. Opening focuses the first control: the reason
 * when one is asked for, otherwise "Anulează" — a stray Enter on a destructive question cancels.
 *
 * <p>A required reason keeps the confirm button disabled until something is typed, the only way of
 * making it mandatory that cannot be missed. Escape and the cancel button both end in `onCancel`.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone,
  busy = false,
  reason,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation(common);
  const dialog = useNativeDialog(open);
  const titleId = useId();
  const reasonMissing = reason?.required === true && !reason.value.trim();

  return (
    <dialog ref={dialog} className="confirm-dialog" aria-labelledby={titleId} onClose={onCancel}>
      <h2 id={titleId}>{title}</h2>
      <p className="confirm-dialog__body">{body}</p>
      {reason && (
        <label className="confirm-dialog__label">
          {reason.label}
          <textarea
            rows={4}
            value={reason.value}
            onChange={(e) => reason.onChange(e.target.value)}
          />
        </label>
      )}
      <div className="confirm-dialog__actions">
        <button type="button" className="btn btn--ghost" onClick={onCancel}>
          {cancelLabel ?? t('cancel')}
        </button>
        <button
          type="button"
          className={CONFIRM_BUTTON[tone]}
          disabled={busy || reasonMissing}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
